import React, { useState, useEffect, useMemo, useContext } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import axios from "axios";
import { TrendingUp, Printer, ArrowLeft, Info, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Alert, AlertTitle, AlertDescription } from "@/components/ui/alert";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
} from "recharts";
import AuthContext from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Student Progress Monitoring — MOSAIC-only chronological 4-domain line graph.
 *
 * DESIGN NOTES (per owner-approved conceptual plan, 2026-10-13):
 *  - Reads /api/classes/my-students for the authorized roster (tenant-scoped by backend).
 *  - Reads /api/growth/student/{id} for the per-student chronological timeline.
 *  - MOSAIC-only. Does NOT merge in NYSESLAT or WIDA ACCESS prior-year scores
 *    (owner choice B=C). Prior-year scores remain on the existing Prior
 *    Assessment History tile.
 *  - Honest handling of edge cases:
 *      * zero administrations  → clear empty state, no chart
 *      * one administration    → single marker visible + explicit "Growth
 *        comparison requires ≥2 administrations" notice; no fabricated trend
 *      * missing domain score  → chart gaps at that date (connectNulls=false)
 *      * missing scored_at     → row skipped and counted in a "skipped" note
 *  - Browser-print only; dedicated @media print block hides nav/picker and
 *    forces the chart + data table to full-width (owner choice C=A).
 */
const DOMAIN_META = [
  { key: "listening_wida", label: "Listening", stroke: "#7c3aed" },
  { key: "speaking_wida",  label: "Speaking",  stroke: "#2563eb" },
  { key: "reading_wida",   label: "Reading",   stroke: "#16a34a" },
  { key: "writing_wida",   label: "Writing",   stroke: "#ea580c" },
];

const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

const StudentProgressMonitor = () => {
  const navigate = useNavigate();
  const [sp, setSp] = useSearchParams();
  const { user } = useContext(AuthContext);

  const [students, setStudents] = useState([]);
  const [studentsLoading, setStudentsLoading] = useState(true);
  const [studentsError, setStudentsError] = useState(null);

  const initialSid = sp.get("student_id") || "";
  const [selectedSid, setSelectedSid] = useState(initialSid);

  const [growth, setGrowth] = useState(null);
  const [growthLoading, setGrowthLoading] = useState(false);
  const [growthError, setGrowthError] = useState(null);

  // Set browser tab title
  useEffect(() => {
    const prev = document.title;
    document.title = "Student Progress Monitoring — MOSAIC";
    return () => { document.title = prev; };
  }, []);

  // Teacher roster
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await axios.get(`${API}/classes/my-students`);
        if (cancelled) return;
        const list = resp.data?.students || [];
        setStudents(list);
      } catch (err) {
        if (cancelled) return;
        setStudentsError(err?.response?.data?.detail || "Unable to load your students.");
      } finally {
        if (!cancelled) setStudentsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Growth timeline for the selected student
  useEffect(() => {
    if (!selectedSid) { setGrowth(null); return; }
    let cancelled = false;
    setGrowthLoading(true);
    setGrowthError(null);
    (async () => {
      try {
        const resp = await axios.get(`${API}/growth/student/${selectedSid}`);
        if (cancelled) return;
        setGrowth(resp.data);
      } catch (err) {
        if (cancelled) return;
        if (err?.response?.status === 403) {
          setGrowthError("You are not authorized to view this student's assessment history.");
        } else if (err?.response?.status === 404) {
          setGrowthError("Student not found.");
        } else {
          setGrowthError(err?.response?.data?.detail || "Unable to load progress data.");
        }
        setGrowth(null);
      } finally {
        if (!cancelled) setGrowthLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedSid]);

  // Keep ?student_id= in sync so the picked student survives refresh / can be shared with a direct URL
  useEffect(() => {
    if (selectedSid) setSp({ student_id: selectedSid }, { replace: true });
  }, [selectedSid]); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedStudent = useMemo(
    () => students.find(s => s.user_id === selectedSid || s.student_id === selectedSid) || null,
    [students, selectedSid]
  );

  // Transform the growth endpoint response into chart rows.
  // Rules:
  //  - Drop rows with no scored_at (count them for a skipped-note).
  //  - Dedupe by exam_session_id (keep first chronological occurrence).
  //  - Preserve nulls for missing domain scores — Recharts will gap the line.
  const { chartRows, skippedCount, totalRows } = useMemo(() => {
    const pts = Array.isArray(growth?.data_points) ? growth.data_points : [];
    const seen = new Set();
    const rows = [];
    let skipped = 0;
    for (const p of pts) {
      if (!p.scored_at) { skipped += 1; continue; }
      const key = p.session_id || `${p.scored_at}:${p.wida_level}`;
      if (seen.has(key)) continue;
      seen.add(key);
      rows.push({
        session_id: p.session_id,
        scored_at: p.scored_at,
        scored_at_label: fmtDate(p.scored_at),
        listening_wida: p.listening_wida ?? null,
        speaking_wida:  p.speaking_wida  ?? null,
        reading_wida:   p.reading_wida   ?? null,
        writing_wida:   p.writing_wida   ?? null,
        wida_level: p.wida_level ?? null,
        wida_level_name: p.wida_level_name ?? "",
        window_name: p.window_name || "",
      });
    }
    rows.sort((a, b) => (a.scored_at < b.scored_at ? -1 : 1));
    return { chartRows: rows, skippedCount: skipped, totalRows: pts.length };
  }, [growth]);

  const studentDisplayName =
    selectedStudent?.name || selectedStudent?.full_name || selectedStudent?.email || selectedSid || "—";

  const onPrint = () => window.print();

  return (
    <div className="max-w-6xl mx-auto px-6 py-10 progress-monitor-root" data-testid="progress-monitor-page">
      {/* Scoped print rules so the Teacher Dashboard chrome, picker chrome, and tooltips drop out
          at print time while the chart + table + legend remain full-width. */}
      <style>{`
        @media print {
          nav, header, aside, footer,
          .recharts-tooltip-wrapper { display: none !important; }
          .no-print { display: none !important; }
          .progress-monitor-root { max-width: 100% !important; padding: 0 !important; }
          .print-block { page-break-inside: avoid; }
          .print-chart-wrap { height: 420px !important; }
          @page { size: letter landscape; margin: 0.5in; }
        }
      `}</style>

      <div className="flex items-center justify-between mb-6 no-print">
        <Button
          variant="outline"
          onClick={() => navigate("/teacher-dashboard")}
          data-testid="pm-back-btn"
        >
          <ArrowLeft size={16} className="mr-2" /> Back to Dashboard
        </Button>
        <Button
          onClick={onPrint}
          disabled={!growth || chartRows.length === 0}
          data-testid="pm-print-btn"
        >
          <Printer size={16} className="mr-2" /> Print Report
        </Button>
      </div>

      <div className="flex items-center gap-3 mb-6">
        <div className="w-12 h-12 bg-[#002FA7] text-white flex items-center justify-center">
          <TrendingUp size={24} />
        </div>
        <div>
          <h1 className="text-3xl font-bold" style={{ fontFamily: "Outfit, sans-serif" }} data-testid="pm-heading">
            Student Progress Monitoring
          </h1>
          <p className="text-sm text-gray-500">
            MOSAIC assessment history across Listening, Speaking, Reading, and Writing — chronological view.
          </p>
        </div>
      </div>

      {/* Student picker */}
      <div className="border-2 border-gray-200 p-6 mb-6 no-print" data-testid="pm-picker-wrap">
        <label className="text-sm font-semibold mb-2 block">Select Student</label>
        {studentsLoading && <p className="text-sm text-gray-500">Loading your students…</p>}
        {studentsError && (
          <Alert variant="destructive" className="mt-2">
            <AlertCircle className="h-4 w-4" />
            <AlertTitle>Unable to load roster</AlertTitle>
            <AlertDescription>{studentsError}</AlertDescription>
          </Alert>
        )}
        {!studentsLoading && !studentsError && (
          students.length === 0 ? (
            <p className="text-sm text-gray-600">
              No students are currently enrolled in your classes.
            </p>
          ) : (
            <Select value={selectedSid} onValueChange={setSelectedSid}>
              <SelectTrigger className="h-12 border-2 rounded-none max-w-xl" data-testid="pm-student-select">
                <SelectValue placeholder="Choose a student from your classes" />
              </SelectTrigger>
              <SelectContent>
                {students.map(s => {
                  const sid = s.user_id || s.student_id;
                  const name = s.name || s.full_name || s.email || sid;
                  const cls = s.class_name || s.class || "";
                  return (
                    <SelectItem key={sid} value={sid} data-testid={`pm-student-opt-${sid}`}>
                      {name}{cls ? ` — ${cls}` : ""}
                    </SelectItem>
                  );
                })}
              </SelectContent>
            </Select>
          )
        )}
      </div>

      {/* Report body */}
      {selectedSid && (
        <div className="print-block">
          <div className="flex items-end justify-between mb-3">
            <div>
              <div className="text-sm text-gray-500">Student</div>
              <div className="text-xl font-bold" data-testid="pm-student-name">{studentDisplayName}</div>
            </div>
            <div className="text-right text-xs text-gray-500">
              <div>Report generated: {new Date().toLocaleString()}</div>
              <div>Teacher: {user?.name || user?.email || "—"}</div>
            </div>
          </div>

          {growthLoading && <p className="text-sm text-gray-500">Loading assessment history…</p>}

          {growthError && (
            <Alert variant="destructive" className="mb-4" data-testid="pm-error">
              <AlertCircle className="h-4 w-4" />
              <AlertTitle>Cannot load progress</AlertTitle>
              <AlertDescription>{growthError}</AlertDescription>
            </Alert>
          )}

          {!growthLoading && !growthError && growth && (
            <>
              {chartRows.length === 0 && (
                <Alert className="mb-4" data-testid="pm-empty">
                  <Info className="h-4 w-4" />
                  <AlertTitle>No MOSAIC assessment history yet</AlertTitle>
                  <AlertDescription>
                    This student has no saved MOSAIC administrations with recorded dates.
                    When a MOSAIC assessment is completed and scored, it will appear here.
                  </AlertDescription>
                </Alert>
              )}

              {chartRows.length === 1 && (
                <Alert className="mb-4" data-testid="pm-single-admin-note">
                  <Info className="h-4 w-4" />
                  <AlertTitle>Only one MOSAIC administration on record</AlertTitle>
                  <AlertDescription>
                    Growth comparison requires at least two administrations. The single
                    administration is shown below as a point for reference.
                  </AlertDescription>
                </Alert>
              )}

              {skippedCount > 0 && (
                <Alert className="mb-4" data-testid="pm-skipped-note">
                  <Info className="h-4 w-4" />
                  <AlertTitle>Some records omitted</AlertTitle>
                  <AlertDescription>
                    {skippedCount} of {totalRows} saved administration(s) have no recorded
                    assessment date and are omitted from the chart.
                  </AlertDescription>
                </Alert>
              )}

              {chartRows.length >= 1 && (
                <div className="border-2 border-gray-200 p-4 mb-4 print-chart-wrap" style={{ height: 420 }} data-testid="pm-chart-wrap">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartRows} margin={{ top: 20, right: 30, left: 10, bottom: 30 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="scored_at_label"
                        angle={-20}
                        textAnchor="end"
                        height={60}
                        tick={{ fontSize: 12 }}
                      />
                      <YAxis
                        domain={[0, 6]}
                        ticks={[1, 2, 3, 4, 5, 6]}
                        label={{ value: "Proficiency (1-6)", angle: -90, position: "insideLeft", style: { fontSize: 12 } }}
                        tick={{ fontSize: 12 }}
                      />
                      <Tooltip formatter={(v) => (v == null ? "—" : v)} />
                      <Legend />
                      {DOMAIN_META.map(d => (
                        <Line
                          key={d.key}
                          type="monotone"
                          dataKey={d.key}
                          name={d.label}
                          stroke={d.stroke}
                          strokeWidth={2}
                          dot={{ r: 4 }}
                          activeDot={{ r: 6 }}
                          connectNulls={false}
                          isAnimationActive={false}
                        />
                      ))}
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Data table — printed copy carries numeric values even if the SVG rasterizes poorly */}
              {chartRows.length >= 1 && (
                <div className="border-2 border-gray-200 overflow-hidden mb-4 print-block" data-testid="pm-data-table-wrap">
                  <table className="w-full text-sm" data-testid="pm-data-table">
                    <thead className="bg-gray-50 border-b-2 border-gray-200">
                      <tr>
                        <th className="text-left p-2">Date</th>
                        <th className="text-left p-2">Window</th>
                        <th className="text-right p-2">Listening</th>
                        <th className="text-right p-2">Speaking</th>
                        <th className="text-right p-2">Reading</th>
                        <th className="text-right p-2">Writing</th>
                        <th className="text-right p-2">Overall</th>
                      </tr>
                    </thead>
                    <tbody>
                      {chartRows.map((r) => (
                        <tr key={r.session_id || r.scored_at} className="border-b border-gray-100" data-testid={`pm-row-${r.session_id || r.scored_at}`}>
                          <td className="p-2">{r.scored_at_label}</td>
                          <td className="p-2 text-gray-600">{r.window_name || "—"}</td>
                          <td className="p-2 text-right">{r.listening_wida ?? "—"}</td>
                          <td className="p-2 text-right">{r.speaking_wida  ?? "—"}</td>
                          <td className="p-2 text-right">{r.reading_wida   ?? "—"}</td>
                          <td className="p-2 text-right">{r.writing_wida   ?? "—"}</td>
                          <td className="p-2 text-right font-semibold">{r.wida_level ?? "—"}{r.wida_level_name ? ` · ${r.wida_level_name}` : ""}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* Disclaimer — same wording guardrails as the Results banner */}
              <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900 print-block" data-testid="pm-disclaimer">
                <Info size={14} className="flex-shrink-0 mt-0.5 text-blue-600" />
                <p>
                  <span className="font-semibold">Interpreting MOSAIC Results:</span>{" "}
                  Practice and instructional indicators, not official state language proficiency
                  scores. Review alongside classroom performance and other measures.
                </p>
              </div>
            </>
          )}
        </div>
      )}

      {!selectedSid && !studentsLoading && students.length > 0 && (
        <div className="text-sm text-gray-500 border border-gray-200 p-6 bg-gray-50">
          Select a student above to view their MOSAIC assessment history and progress chart.
        </div>
      )}
    </div>
  );
};

export default StudentProgressMonitor;
